import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'

type Props = {
  bounds: { min: number; max: number }
  value: { min: string; max: string }
  setValue: Dispatch<SetStateAction<{ min: string; max: string }>>
  language: 'it' | 'en'
}

const roundPrice = (value: number) => Math.round(value * 100) / 100

const formatPrice = (value: number, language: 'it' | 'en') =>
  new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value)

const PriceRangeSlider = ({ bounds, value, setValue, language }: Props) => {
  const min = roundPrice(bounds.min)
  const max = roundPrice(Math.max(bounds.max, min))
  const [draftRange, setDraftRange] = useState(value)
  const draftRangeRef = useRef(value)
  useEffect(() => {
    draftRangeRef.current = value
    setDraftRange(value)
  }, [value.max, value.min])

  const selectedMin = Math.min(Math.max(Number(draftRange.min) || min, min), max)
  const selectedMax = Math.max(Math.min(Number(draftRange.max) || max, max), selectedMin)
  const step = max - min > 100 ? 1 : 0.01

  const updateDraftRange = (next: { min: string; max: string }) => {
    draftRangeRef.current = next
    setDraftRange(next)
  }

  const commitDraftRange = () => {
    setValue(draftRangeRef.current)
  }

  const updateMin = (next: number) => {
    const normalized = roundPrice(Math.min(Math.max(next, min), selectedMax))
    updateDraftRange({
      ...draftRangeRef.current,
      min: normalized <= min ? '' : String(normalized),
    })
  }

  const updateMax = (next: number) => {
    const normalized = roundPrice(Math.max(Math.min(next, max), selectedMin))
    updateDraftRange({
      ...draftRangeRef.current,
      max: normalized >= max ? '' : String(normalized),
    })
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-xs font-semibold text-slate-700">
        <span>{formatPrice(selectedMin, language)}</span>
        <span>{formatPrice(selectedMax, language)}</span>
      </div>
      <div className="relative h-7" aria-label={language === 'en' ? 'Price range' : 'Intervallo di prezzo'}>
        <div className="pointer-events-none absolute left-4 right-4 top-3 h-1 rounded-full bg-slate-200">
          <div
            className="absolute inset-y-0 rounded-full bg-cyan-600"
            style={{
              left: `${max === min ? 0 : ((selectedMin - min) / (max - min)) * 100}%`,
              right: `${max === min ? 0 : 100 - ((selectedMax - min) / (max - min)) * 100}%`,
            }}
          />
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={selectedMin}
          aria-label={language === 'en' ? 'Minimum price' : 'Prezzo minimo'}
          onChange={(event) => updateMin(Number(event.target.value))}
          onPointerUp={commitDraftRange}
          onKeyUp={commitDraftRange}
          onBlur={commitDraftRange}
          className="pointer-events-none absolute inset-0 h-7 w-full appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-cyan-700 [&::-moz-range-thumb]:shadow [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-cyan-700 [&::-webkit-slider-thumb]:shadow"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={selectedMax}
          aria-label={language === 'en' ? 'Maximum price' : 'Prezzo massimo'}
          onChange={(event) => updateMax(Number(event.target.value))}
          onPointerUp={commitDraftRange}
          onKeyUp={commitDraftRange}
          onBlur={commitDraftRange}
          className="pointer-events-none absolute inset-0 h-7 w-full appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-cyan-700 [&::-moz-range-thumb]:shadow [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-cyan-700 [&::-webkit-slider-thumb]:shadow"
        />
      </div>
      <div className="flex justify-between text-[11px] text-slate-400">
        <span>{formatPrice(min, language)}</span>
        <span>{formatPrice(max, language)}</span>
      </div>
    </div>
  )
}

export default PriceRangeSlider
